import { createMiddleware } from '@tanstack/react-start';
import type { Profile } from '@/apiTypes/profile';

/**
 * Shape of the resolved authenticated user injected into a server-fn handler's
 * `context.auth` by the middleware below. Mirrors the previous `requireAuth()`
 * return type so call sites compose unchanged.
 */
export type AuthenticatedUser = {
  supabaseUser: { id: string; email: string | undefined };
  profile: Profile;
};

/**
 * Middleware module split rationale:
 * The middleware factory file must be importable from the client because
 * `createServerFn().middleware([...])` produces a server-fn proxy whose RPC
 * call path on the client walks the middleware chain via `flattenMiddlewares`.
 * If we put real prisma/supabase imports at module scope here, those would
 * end up reachable from the client bundle and trip TanStack's import
 * protection. The actual auth resolution lives in
 * `./authMiddleware.impl.server.ts` and is `import()`-ed from inside the
 * `.server(...)` callback — the callback only runs on the server, so the
 * impl chunk is never loaded client-side.
 */

/**
 * Function-level middleware that requires an authenticated user and injects
 * `context.auth = { supabaseUser, profile }` into the handler. Throws
 * "Unauthorized" if no user is resolvable.
 */
export const authMiddleware = createMiddleware({ type: 'function' }).server(
  async ({ next }) => {
    const { resolveAuthOrThrow } = await import('./authMiddleware.impl.server');
    const auth = await resolveAuthOrThrow();
    return next({ context: { auth } });
  },
);

/**
 * Function-level middleware that injects `context.auth = AuthenticatedUser | null`
 * into the handler — never throws, lets the handler decide what to do for
 * anonymous viewers.
 */
export const optionalAuthMiddleware = createMiddleware({
  type: 'function',
}).server(async ({ next }) => {
  const { resolveOptionalAuth } = await import('./authMiddleware.impl.server');
  const auth = await resolveOptionalAuth();
  return next({ context: { auth } });
});
